/**
 * Dashboard — the console home (WSRPDM-0017): a system-wide overview
 * aggregating queue depth, engine state, the attention feed, and roster
 * health. Replaces the WSRPDM-0011 `SectionPlaceholder` for `/dashboard`,
 * the last Phase-2 screen — reuses every API client from WSRPDM-0012–0015,
 * no new client module (contract §§1.1, 2.1, 3.1, 4.1).
 *
 * Fetches all four sources (queue summary, schedule config, notifications,
 * tenant roster) in parallel on mount via four independent
 * `useDashboardSource` instances — each card renders off ONE source's own
 * loading/error, so a slow or failing source degrades only the card(s) that
 * depend on it, never the whole page. The KPI row is the one place two
 * sources meet (summary + roster): the "Enabled tenants" tile depends only
 * on the roster and stays correct even if the summary fails, and vice versa
 * for the other four tiles.
 *
 * No polling (spec Non-Goal — no time-series data exists to justify it); a
 * per-card Retry re-runs just that source via `useDashboardSource`'s
 * `refetch`, mirroring the retry affordance every other screen already has.
 */
import { Alert, Box, Grid, Link, Paper, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useCopy } from '@/hooks/useCopy';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { KpiCard } from '@/components/ui';
import { getSummary } from '@/api/wsr/queue';
import { getConfig } from '@/api/wsr/scheduleConfig';
import { listNotifications } from '@/api/wsr/notifications';
import { listTenants } from '@/api/wsr/tenants';
import { formatSecondsDuration } from './queue/duration';
import { useDashboardSource } from './dashboard/useDashboardSource';
import { DepthTable } from './dashboard/DepthTable';
import { EngineStateCard } from './dashboard/EngineStateCard';
import { NeedsAttentionCard } from './dashboard/NeedsAttentionCard';

/** A section's retry affordance — mirrors Queue.tsx/Notifications.tsx's inline-button Alert. */
function RetryAlert({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useCopy();
  return (
    <Alert
      severity="error"
      action={
        <Typography
          component="button"
          variant="caption"
          onClick={onRetry}
          sx={{
            border: 0,
            background: 'none',
            cursor: 'pointer',
            textDecoration: 'underline',
            color: 'inherit',
          }}
        >
          {t('common.retry', 'Retry')}
        </Typography>
      }
    >
      {message}
    </Alert>
  );
}

export function Dashboard() {
  const { t } = useCopy();

  const summarySource = useDashboardSource(getSummary);
  const configSource = useDashboardSource(getConfig);
  const notificationsSource = useDashboardSource(() =>
    listNotifications({ status: 'OPEN', size: 3 }),
  );
  const tenantsSource = useDashboardSource(listTenants);

  const summary = summarySource.data;
  const enabledTenantsCount = tenantsSource.data
    ? tenantsSource.data.tenants.filter((tenant) => tenant.tenantEnabled).length
    : null;
  const totalTenantsCount = tenantsSource.data?.tenants.length ?? null;

  return (
    <Box>
      <Typography variant="h1" component="h1" gutterBottom>
        {t('page.dashboard.title', 'Dashboard')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t('page.dashboard.subtitle', 'Fleet health at a glance.')}
      </Typography>

      <Stack
        direction="row"
        spacing={2}
        role="group"
        aria-label={t('dashboard.kpiRow.ariaLabel', 'Fleet KPIs')}
        sx={{ mb: 3, flexWrap: 'wrap' }}
      >
        <Box sx={{ flex: '1 1 180px' }}>
          <KpiCard
            label={t('dashboard.kpi.enabledTenants', 'Enabled tenants')}
            value={
              enabledTenantsCount !== null && totalTenantsCount !== null
                ? t('dashboard.kpi.enabledTenantsValue', '{{enabled}} of {{total}}', {
                    enabled: enabledTenantsCount,
                    total: totalTenantsCount,
                  })
                : '—'
            }
          />
        </Box>
        <Box sx={{ flex: '1 1 180px' }}>
          <KpiCard label={t('queue.kpi.pending', 'Pending')} value={summary?.pending ?? '—'} />
        </Box>
        <Box sx={{ flex: '1 1 180px' }}>
          <KpiCard label={t('queue.kpi.inFlight', 'In flight')} value={summary?.inFlight ?? '—'} />
        </Box>
        <Box sx={{ flex: '1 1 180px' }}>
          <KpiCard
            label={t('queue.kpi.oldestPending', 'Oldest pending')}
            value={
              summary
                ? summary.oldestPendingAgeSeconds !== null
                  ? formatSecondsDuration(summary.oldestPendingAgeSeconds)
                  : '—'
                : '—'
            }
          />
        </Box>
        <Box sx={{ flex: '1 1 180px' }}>
          <KpiCard
            label={t('queue.kpi.failedLast24h', 'Failed (24h)')}
            value={summary?.failedLast24h ?? '—'}
          />
        </Box>
      </Stack>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" component="h2" gutterBottom>
              {t('dashboard.depthTable.title', 'Depth by job type')}
            </Typography>
            {summarySource.loading ? <LoadingSpinner /> : null}
            {!summarySource.loading && summarySource.error ? (
              <RetryAlert
                message={t('dashboard.depthTable.loadError', 'Could not load queue depth.')}
                onRetry={summarySource.refetch}
              />
            ) : null}
            {!summarySource.loading && !summarySource.error ? (
              (summary?.byJobType.length ?? 0) === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  {t('dashboard.depthTable.empty', 'No job-type data yet.')}
                </Typography>
              ) : (
                <DepthTable rows={summary?.byJobType ?? []} />
              )
            ) : null}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack
              direction="row"
              sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
            >
              <Typography variant="h6" component="h2">
                {t('dashboard.engineState.title', 'Engine state')}
              </Typography>
              <Link component={RouterLink} to="/engine" variant="body2">
                {t('dashboard.engineState.manageLink', 'Manage in Engine')}
              </Link>
            </Stack>
            {configSource.loading ? <LoadingSpinner /> : null}
            {!configSource.loading && configSource.error ? (
              <RetryAlert
                message={t('dashboard.engineState.loadError', 'Could not load engine state.')}
                onRetry={configSource.refetch}
              />
            ) : null}
            {!configSource.loading && !configSource.error && configSource.data ? (
              <EngineStateCard config={configSource.data} />
            ) : null}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack
              direction="row"
              sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
            >
              <Typography variant="h6" component="h2">
                {t('dashboard.needsAttention.title', 'Needs attention')}
              </Typography>
              <Link component={RouterLink} to="/notifications" variant="body2">
                {t('dashboard.needsAttention.viewAll', 'View all notifications')}
              </Link>
            </Stack>
            {notificationsSource.loading ? <LoadingSpinner /> : null}
            {!notificationsSource.loading && notificationsSource.error ? (
              <RetryAlert
                message={t('dashboard.needsAttention.loadError', 'Could not load notifications.')}
                onRetry={notificationsSource.refetch}
              />
            ) : null}
            {!notificationsSource.loading && !notificationsSource.error ? (
              <NeedsAttentionCard
                notifications={notificationsSource.data?.content ?? []}
                refetch={notificationsSource.refetch}
              />
            ) : null}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
